import * as React from 'react'
import { useInfiniteQuery, QueryFunctionContext } from 'react-query'
import { RouteComponentProps, useHistory } from 'react-router-dom'
import { getFaqList } from '@feedback/services'
import InfiniteScroll from 'react-infinite-scroll-component'
import { Icon } from 'antd-mobile'
import debounce from 'lodash/debounce'
import SearchInput from '@feedback/components/SearchInput'
import {
  getLangKey,
  getTextByKey,
  reportFn,
  tracking
} from '@feedback/utils/util'
import { getAppConfig } from '@feedback/utils/CONST'
import './index.scss'
import clsx from 'clsx'

const Header: React.FC<{
  scrollTop: number
}> = ({ scrollTop }) => {
  const isSticky = scrollTop >= (350 - 192) / 2
  const calcOpacity = scrollTop <= 0 ? 1 : 7.2 / scrollTop
  const history = useHistory()
  const { appid, logoShadow, bg, bgSearch, poster } = getAppConfig() || {}

  return (
    <>
      {isSticky ? <div style={{ height: 79 }}>&nbsp;</div> : null}
      <header className={clsx('faq-header', isSticky && 'is-sticky')}>
        {!isSticky ? (
          <>
            {bg && (
              <div
                className='faq-header__bg'
                style={{ backgroundImage: `url(${bg})` }}
              />
            )}
            {poster && (
              <div
                className='faq-header__poster'
                style={{
                  opacity: calcOpacity,
                  backgroundImage: `url(${poster})`
                }}
              />
            )}
            {scrollTop <= 10 && !isSticky ? (
              <section>
                <h1>{getTextByKey('hello')}</h1>
                <h2>{getTextByKey('What can we do to help')}</h2>
              </section>
            ) : null}
          </>
        ) : (
          <>
            <div
              className='faq-header__bg-search'
              style={{ backgroundImage: `url(${bgSearch})` }}
            />
            <div className='faq-header__title'>&nbsp;</div>
          </>
        )}
        {logoShadow && (
          <div
            className={clsx(
              'faq-header__logo',
              'is-shadow',
              scrollTop > 0 ? 'is-fixed' : ''
            )}
            style={{ backgroundImage: `url(${logoShadow})` }}
          />
        )}
        <div className={'faq-header__search'}>
          <SearchInput
            onSearch={(keyWord) => {
              history.push(
                `/searchResult?${new URLSearchParams({
                  keyWord
                }).toString()}`
              )
            }}
          />
        </div>
        {['cm1006', 'cm1007']?.includes(appid) && (
          <div className='round-b'></div>
        )}
      </header>
    </>
  )
}

const Menu = ({ scrolling }: { scrolling: boolean }): JSX.Element => {
  const history = useHistory()
  const langKey = getLangKey()
  const isRTL = langKey === 'ar-SA'
  const { appid } = getAppConfig() || {}

  return (
    <ul
      className={clsx('faq-menu', scrolling && 'is-active', {
        'is-rtl': isRTL
      })}
    >
      <li
        onClick={() => {
          reportFn({ parm1: 1, parm2: 2, parm6: '' })
          history.push('/ticketList')
        }}
      >
        <i className={'faq-menu__icon--history'} />
        {getTextByKey('feedback history')}
      </li>
      <li
        onClick={() => {
          if (appid === 'cm1006') {
            tracking({
              _step_id: '',
              _feedback_page_id: 1,
              _postion_id: 3,
              _search_words: '',
              _faq_id: '',
              _src_faq_id: ''
            })
          } else {
            reportFn({ parm1: 1, parm2: 3, parm6: '' })
          }
          history.push('/ticketForm')
        }}
      >
        <i className={'faq-menu__icon--contact'} />
        {getTextByKey('Contact Us')}
      </li>
    </ul>
  )
}

const NetWork = ({ scrolling }: { scrolling: boolean }): JSX.Element => {
  const history = useHistory()
  const langKey = getLangKey()
  const isRTL = langKey === 'ar-SA'

  return (
    <ul className={clsx('faq-network', scrolling && 'is-active', {
      'is-rtl': isRTL
    })}>
      <li
        onClick={() => {
          history.push('/networkCheck')
        }}
      >
        <i className={'faq-network__icon'} />
        {getTextByKey('Network Status')}
      </li>
    </ul>
  )
}

export interface IQuestion {
  _id: string
  question: string
  star: number
  type_id: number
}

export interface IQuestionList {
  faq_type: string
  children: IQuestion[]
}

export const getQuestionList = async ({
  pageParam: page = 1,
  limit = 10
}: { limit?: number } & QueryFunctionContext): Promise<{
  list: IQuestionList[]
  total: number
}> => {
  const { appid } = getAppConfig() || {}
  const { code, data, message } = (await getFaqList({ page, limit })) || {}

  if (code !== 0) {
    return Promise.reject(new Error(message))
  }

  if (appid === 'cm1006') {
    tracking({
      _step_id: '',
      _feedback_page_id: 1,
      _postion_id: 5,
      _search_words: '',
      _faq_id: '',
      _src_faq_id: ''
    })
  } else {
    reportFn({ parm1: 1, parm2: 5, parm6: '' })
  }

  return {
    list: Array.isArray(data)
      ? data
      : Array.isArray(data?.list)
      ? data.list
      : [],
    total: data?.count ? data?.count : 10
  }
}

export const QuestionList: React.FC = () => {
  const {
    data,
    error,
    isError,
    isLoading,
    hasNextPage,
    fetchNextPage,
    refetch
  } = useInfiniteQuery<{ list: IQuestionList[]; total: number }, Error>(
    'getQuestionList',
    getQuestionList,
    {
      getNextPageParam: (lastPage, allPages) => {
        const allPagesLength = allPages.reduce((counter, page) => {
          return counter + page.list.length
        }, 0)
        return allPagesLength < lastPage.total ? allPages.length + 1 : null
      }
    }
  )
  const [isRefetching, setIsRefetching] = React.useState(false)
  const history = useHistory()
  const { appid } = getAppConfig() || {}

  if (isLoading) {
    return (
      <div className={'faq-list__more'}>
        <Icon type='loading' />
      </div>
    )
  }

  if (isError) {
    return <div className={'faq-list__more'}>{error?.message}</div>
  }

  if (!data || data.pages.length < 1) {
    return (
      <div className={'faq-list__more'}>
        {getTextByKey('Nothing more to load')}
      </div>
    )
  }

  const dataLength = data.pages.reduce((counter, page) => {
    return counter + page.list.length
  }, 0)

  return (
    <>
      <InfiniteScroll
        dataLength={dataLength}
        hasMore={!!hasNextPage}
        next={() => {
          // fetchNextPage()
        }}
        loader={
          null
          // !!dataLength && (
          //   <div className={'faq-list__more is-bottom'}>
          //     <Icon type='loading' />
          //   </div>
          // )
        }
        pullDownToRefresh={false}
        pullDownToRefreshThreshold={50}
        pullDownToRefreshContent={
          <div className={'faq-list__more'}>
            <Icon type='loading' />
          </div>
        }
        releaseToRefreshContent={
          <div className={'faq-list__more'}>
            <Icon type='loading' />
          </div>
        }
        refreshFunction={() => {
          // setIsRefetching(true)
          // refetch({ cancelRefetch: true }).then(() => setIsRefetching(false))
        }}
      >
        {isRefetching && (
          <div className={'faq-list__more is-top'}>
            <Icon type='loading' />
          </div>
        )}
        {data?.pages?.map((page, index) => (
          <React.Fragment key={index}>
            {Array.isArray(page.list) &&
              page.list?.map(({ faq_type, children }, idx) => (
                <ul className={'faq-list'} key={`${idx}-${faq_type}`}>
                  <h2>{faq_type}</h2>
                  {children?.map(({ question, _id }) => (
                    <li
                      key={_id}
                      onClick={() => {
                        reportFn({ parm1: 1, parm2: 1, parm6: _id })

                        if (appid === 'cm1006') {
                          tracking({
                            _step_id: '',
                            _feedback_page_id: 1,
                            _postion_id: 2,
                            _search_words: '',
                            _faq_id: _id,
                            _src_faq_id: ''
                          })
                        } else {
                          reportFn({ parm1: 1, parm2: 1, parm6: _id })
                        }

                        history.push(`/questionDetail?faq_id=${_id}`)
                      }}
                    >
                      {question}
                    </li>
                  ))}
                </ul>
              ))}
          </React.Fragment>
        ))}
        {(!dataLength || !hasNextPage) && (
          <div className={`faq-list__more ${dataLength ? 'is-bottom' : ''}`}>
            {getTextByKey('Nothing more to load')}
          </div>
        )}
      </InfiniteScroll>
    </>
  )
}

interface IProps extends RouteComponentProps {
  [key: string]: any
}

const QuestionIndex: React.FC<IProps> = () => {
  const { appid } = getAppConfig() || {}
  const langKey = getLangKey()
  const [scrolling, setScrolling] = React.useState(false)
  const [scrollTop, setScrollTop] = React.useState(0)
  const endScrolling = debounce((value) => setScrolling(value), 250)

  const onScroll = (event: any) => {
    setScrollTop(event.target?.documentElement?.scrollTop || 0)
    setScrolling(event.target?.documentElement?.scrollTop > scrollTop)
    endScrolling(false)
  }

  React.useEffect(() => {
    window.addEventListener('scroll', onScroll)

    return () => {
      window.removeEventListener('scroll', onScroll)
    }
  }, [])

  return (
    <div className={clsx('faq-container', appid)}>
      <Header scrollTop={scrollTop} />
      <QuestionList />
      {appid === 'cm1001' && <NetWork scrolling={scrolling} />}
      <Menu scrolling={scrolling} />
    </div>
  )
}

export default QuestionIndex
